import axios from 'axios';

import { getEffectiveApiKey, getEffectiveUser, SYNC_AUTH_EVENT } from './syncApi';

// The categorisation endpoints live under the same backend host as the rest of
// the sync console. We re-use the same env var so a single backend URL keeps
// working for both consoles.
const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const categoriesApi = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

categoriesApi.interceptors.request.use((config) => {
  const apiKey = getEffectiveApiKey();
  const user = getEffectiveUser();
  if (apiKey) config.headers['X-Sync-API-Key'] = apiKey;
  if (user) config.headers['X-User'] = user;
  return config;
});

// Re-broadcast auth errors so the sidebar credentials card pulses on 401/503
// in this console too (consistent with syncApi).
categoriesApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 503) {
      try {
        window.dispatchEvent(
          new CustomEvent(SYNC_AUTH_EVENT, {
            detail: {
              status,
              detail: error?.response?.data?.detail || error?.message || 'auth failed',
              hasKey: Boolean(getEffectiveApiKey()),
            },
          }),
        );
      } catch {
        // ignore
      }
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Read endpoints (no auth required)
// ---------------------------------------------------------------------------

export const getCategoryTree = () =>
  categoriesApi.get('/categories/tree').then((r) => r.data);

export const getUncategorisedProducts = (params = {}) =>
  categoriesApi.get('/categories/uncategorised', { params }).then((r) => r.data);

export const getProductsInCategory = (code, params = {}) =>
  categoriesApi
    .get(`/categories/${encodeURIComponent(code)}/products`, { params })
    .then((r) => r.data);

export const getSkuCategorisation = (sku) =>
  categoriesApi
    .get(`/products/${encodeURIComponent(sku)}/categorisation`)
    .then((r) => r.data);

// ---------------------------------------------------------------------------
// Write endpoints (require_api_key)
// ---------------------------------------------------------------------------

export const updateSkuCategorisation = (sku, payload) =>
  categoriesApi
    .put(`/products/${encodeURIComponent(sku)}/categorisation`, payload)
    .then((r) => r.data);

export const bulkCategorise = (payload) =>
  categoriesApi.post('/products/categorisation/bulk', payload).then((r) => r.data);
